/**
 * React Query hook for send-timing recommendations.
 *
 * Used by:
 *   - components/communications/ml/OptimalSendTimeBadge.tsx
 *   - components/communications/insights/OptimalSendTimeHeatmapCard.tsx
 *   - components/v2/member/RecommendedNextSendCard.tsx
 */

import { useQuery } from '@tanstack/react-query';
import { authFetch } from '@/lib/authFetch';
import type { SendTimingMetric, SendTimingRecs } from './types';

const STALE_MS = 30 * 60 * 1000; // 30 min — recommendations don't change intraday

interface UseSendTimingOptions {
  language?: string;
  topN?: number;
  metric?: SendTimingMetric;
  enabled?: boolean;
}

async function fetchRecs(
  language?: string,
  topN: number = 10,
  metric: SendTimingMetric = 'engagement_rate',
): Promise<SendTimingRecs> {
  const params = new URLSearchParams({ top_n: String(topN), metric });
  if (language) params.set('language', language);
  const resp = await authFetch(
    `/api/legacy-trust-ml/send-timing/recommendations?${params.toString()}`,
  );
  if (resp.status === 503) {
    throw new Error('model_not_trained');
  }
  if (!resp.ok) {
    throw new Error(`send-timing recs failed: ${resp.status}`);
  }
  return resp.json();
}

export function useSendTimingRecs({
  language,
  topN = 10,
  metric = 'engagement_rate',
  enabled = true,
}: UseSendTimingOptions = {}) {
  return useQuery<SendTimingRecs>({
    queryKey: ['legacy-trust-ml', 'send-timing', language ?? 'auto', topN, metric],
    queryFn: () => fetchRecs(language, topN, metric),
    enabled,
    staleTime: STALE_MS,
    retry: (failureCount, err) => {
      if (err instanceof Error && err.message === 'model_not_trained') return false;
      return failureCount < 2;
    },
  });
}
